/**
* @summary - License Certificate List
* @author Yuvasri S
* @since 2024-12-20
*/

import React, { useState, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CustomDialog from '../commoncomponents/CustomDialog';
import { useNavigate } from 'react-router-dom';
import { DefaultCard } from '../../assets/styled-components/card';
import CreateNewButton from '../commoncomponents/CreateNewButton';
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import CloseButton from '../commoncomponents/CloseButton';
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import { dialogContent } from '../../utility/message';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import LicenseCertificateRepository from '../../api/repositories/licenseCertificateRepository';

function LicenseCertificateList() {

    const [rows, setRows] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [filteredRows, setFilteredRows] = useState([]);
    const [rowToDelete, setRowToDelete] = useState(null);
    const navigate = useNavigate();
    const [content, setContent] = useState();
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filterModel, setFilterModel] = useState([]);
    const [sortModel, setSortModel] = useState([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [exportData, setExportData] = useState([]);

    const serviceOptions1 = [
        { label: "FEMS", value: "FEMS", id: 1 },
        { label: "BEMS", value: "BEMS", id: 2 },
    ];
    const [service1, setService1] = useState(serviceOptions1[0]);

    const transformedRows = filteredRows.map(row => ({
        ...row,
        licnexpirydateformat: row.licnexpirydate
            ? new Date(row.licnexpirydate).toLocaleDateString('en-GB')
            : 'N/A',

        isactiveformate: row.isactive ? 'Active' : 'Inactive',
    }));

    const columns = [
        { field: 'licnno', headerName: 'License No', width: 150 },
        { field: 'categoryName', headerName: 'License Category', width: 150 },
        { field: 'licntypecode', headerName: 'License Type Code', width: 150 },
        { field: 'licntypevalue', headerName: 'License Type Code Description', width: 150 },
        { field: 'licnclassgrade', headerName: 'Class/Grade', width: 150 },
        {
            field: 'licnexpirydateformat', headerName: 'Expiry Date', width: 150, filter: false,
            sort: false
        },
        { field: 'isactiveformate', headerName: 'Status', width: 150 },
    ];

    const fetchCalibrationCertificate = async () => {
        setIsLoading(true);
        try {
            const response = await LicenseCertificateRepository.licenseCertificateList(service1.value, pageIndex, pageSize, filterModel, sortModel);
            setRowCount(response.data.totalCount);
            setRows(response.data.items);
            setFilteredRows(response.data.items);
        } catch (error) {
            // Handle errors
        } finally {
            setIsLoading(false);
        }
    };

    const fetchExportdata = async () => {
        setIsLoading(true);
        try {
            const response = await LicenseCertificateRepository.licenseCertificateExport(
                service1.value,
                filterModel,
                sortModel
            );

            // Format the dates before setting the export data
            const formattedData = response.data.map((row) => ({
                ...row,
                licnexpirydateformat: row.licnexpirydate
                    ? new Date(row.licnexpirydate).toLocaleDateString('en-GB')
                    : 'N/A',
            }));

            setExportData(formattedData);
        } catch (error) {
            console.error("Error fetching export data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteCalibrateCertificate = async (id) => {
        try {
            const response = await LicenseCertificateRepository.deleteLicenseCertificate(id);
            if (response.status === 204) {
                setConfirmationDialogOpen(true)
                setContent('Record deleted successfully.')
            }
            fetchCalibrationCertificate();
        }
        catch (error) {

            if (error.response && error.response.status === 400) {
                setConfirmationDialogOpen(true);

                setContent(error ? error.response.data.detail : "Error Occured!")
            }
        }
    };

    const handleRefresh = () => {
        setPageIndex(1);
        setPageSize(10);
        setFilterModel([])
        setSortModel([])
    };

    const handleServiceChange1 = (newValue) => {
        setService1(newValue);
        setPageIndex(1);
        setPageSize(10);
        setFilterModel([]);
        setSortModel([]);
    };
    const handleCreateLicenseCertificate = () => {
        navigate("register", { state: { title: 'Create License Certificate Details' } });
    };

    const handleEdit = (rowData) => {
        navigate("register", { state: { ...rowData, editmode: true, title: 'Edit License Certificate Details' } });
    };

    const handleView = (rowData) => {
        navigate("register", { state: { ...rowData, viewmode: true, title: 'View License Certificate Details' } });
    };
    useEffect(() => {
        fetchCalibrationCertificate();
    }, [filterModel, sortModel, pageIndex, pageSize]);

    const confirmDelete = async () => {
        if (rowToDelete) {
            await deleteCalibrateCertificate(rowToDelete.id);
            setDeleteDialogOpen(false);
            setConfirmationDialogOpen(true);
            setRowToDelete(null);
        }
    };
    const handleDelete = (row) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    };

    return (
        <>
            <PageTitle title="License Certificate Details" />
            <DefaultCard>
                <Box
                    mb={1}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                    }}
                >
                    <SectionTitle title="License Certificate Details List" />
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                        <CustomSelectInput
                            fullWidth={false}
                            labelSize={"14px"}
                            sx={{ width: 150 }}
                            label={"Service"}
                            options={serviceOptions1}
                            value={service1}
                            onChange={handleServiceChange1}
                            placeholder={"Select Service"}
                            highlight={true}
                        />
                        <CreateNewButton onClick={handleCreateLicenseCertificate} />
                        <RefreshExportButtons
                            export={exportData}
                            exportFunction={fetchExportdata}
                            exportData={exportData}
                            refData={gridApi}
                            handleRefresh={handleRefresh}
                            title="License Certificate Details"
                        />
                    </Stack>
                </Box>
                <CustomAgGrid
                    rows={transformedRows}
                    columns={columns}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    handleView={handleView}
                    setGridApi={setGridApi}
                    isLoading={isLoading}
                    ref={gridApi}
                    pagination={false}
                    setFilterModel={setFilterModel}
                    setSortModel={setSortModel}
                    totalCount={rowCount}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    setPageIndex={setPageIndex}
                    setPageSize={setPageSize}
                />
                <CustomDialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}

                    onConfirm={confirmDelete}
                    content={dialogContent.confirmDelete}
                    cancelBtn='No'
                    confirmBtn="Yes"
                />
                <CustomDialog
                    open={confirmationDialogOpen}
                    onClose={() => setConfirmationDialogOpen(false)}
                    content={
                        content
                    }
                    confirmBtn="OK"
                    onConfirm={() => setConfirmationDialogOpen(false)}
                />

            </DefaultCard>
            <CloseButton />

        </>
    );
}

export default LicenseCertificateList;






